import React from "react";
import { CheckCircle, MessageCircle, X } from "lucide-react";
import "./CallReadinessModal.css";
import { getLeadStatus } from "../utils/leadReadiness";

function CallReadinessModal({
  isOpen,
  onClose,
  callReadyLeads,
  callReadyStats,
  activeStatusFilter,
  onStatusFilterChange,
  resolveDrr,
  resolveStoreMeta,
  buildClientSummary,
  buildCandidateSummary,
  handleCopySummary,
  copiedSummaryId,
  onArchiveLead,
}) {
  if (!isOpen) return null;

  const isIntakeLead = (lead) => {
    const leadPayload = lead?.lead || lead || {};
    const mode =
      (leadPayload.recruitment_mode ||
        leadPayload.recruitmentMode ||
        lead.recruitment_mode ||
        lead.recruitmentMode ||
        "") || "";
    const template = String(leadPayload.template || lead.template || "").toLowerCase();
    return String(mode).toLowerCase() === "intake" || template.includes("intake");
  };

  const useIntakeCopy =
    Array.isArray(callReadyLeads) &&
    callReadyLeads.length > 0 &&
    callReadyLeads.every((lead) => isIntakeLead(lead));

  const handleStatusFilter = (status) => {
    if (!onStatusFilterChange) return;
    onStatusFilterChange((current) => (current === status ? "all" : status));
  };

  const formatRelativeTime = (value) => {
    if (!value) return "-";
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return "-";
    const diffMs = Date.now() - parsed.getTime();
    const diffSeconds = Math.floor(diffMs / 1000);
    if (diffSeconds < 60) return "Completed just now";
    const diffMinutes = Math.floor(diffSeconds / 60);
    if (diffMinutes < 60)
      return `Completed ${diffMinutes} minute${diffMinutes === 1 ? "" : "s"} ago`;
    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours < 24)
      return `Completed ${diffHours} hour${diffHours === 1 ? "" : "s"} ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7)
      return `Completed ${diffDays} day${diffDays === 1 ? "" : "s"} ago`;
    return `Completed ${parsed.toLocaleDateString()}`;
  };

  const uniqueList = (items) => {
    if (!Array.isArray(items)) return [];
    const seen = new Set();
    return items.filter((item) => {
      if (!item) return false;
      const isObject = typeof item === "object";
      const label = isObject ? String(item.label || "").trim() : "";
      const value = isObject ? String(item.value || "").trim() : "";
      const key = isObject
        ? `${label.toLowerCase()}|${value.toLowerCase()}`
        : String(item).trim().toLowerCase();
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  const cleanText = (value) => String(value || "").trim();

  const intakeFieldRows = (drr) => {
    const profile = drr?.intake_profile || {};
    const rows = [
      ["Route", profile.route],
      ["Company", profile.company],
      ["People count", profile.people_count],
      ["Start timing", profile.start_timing],
      ["Preferred contact", profile.preferred_contact],
      ["Brief details", profile.enquiry_details],
    ];
    return rows.filter(([, value]) => cleanText(value));
  };

  const filteredLeads =
    activeStatusFilter && activeStatusFilter !== "all"
      ? callReadyLeads.filter(
          (lead) => getLeadStatus(resolveDrr(lead)) === activeStatusFilter
        )
      : callReadyLeads;

  return (
    <div className="modal-overlay">
      <div
        className="modal-box call-readiness-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="call-readiness-title"
      >
        <button
          type="button"
          className="modal-close-button"
          onClick={onClose}
          aria-label="Close dialog"
        >
          <X size={18} />
        </button>
        <div className="modal-header-icon">
          <CheckCircle size={28} />
        </div>
        {callReadyLeads.length > 0 && (
          <div className="call-readiness-overview">
            <button
              type="button"
              className={`call-readiness-pill ready${
                activeStatusFilter === "ready" ? " is-active" : ""
              }`}
              onClick={() => handleStatusFilter("ready")}
            >
              <span className="call-readiness-dot" />
              {useIntakeCopy ? "Ready for follow-up" : "Ready to Contact"} ({callReadyStats.ready})
            </button>
            <button
              type="button"
              className={`call-readiness-pill needs${
                activeStatusFilter === "needs" ? " is-active" : ""
              }`}
              onClick={() => handleStatusFilter("needs")}
            >
              <span className="call-readiness-dot" />
              {useIntakeCopy ? "Missing key detail" : "Needs Clarification"} ({callReadyStats.needs})
            </button>
            <button
              type="button"
              className={`call-readiness-pill mismatch${
                activeStatusFilter === "mismatch" ? " is-active" : ""
              }`}
              onClick={() => handleStatusFilter("mismatch")}
            >
              <span className="call-readiness-dot" />
              {useIntakeCopy ? "Potential blocker" : "Likely Mismatch"} ({callReadyStats.mismatch})
            </button>
          </div>
        )}
        {callReadyLeads.length === 0 ? (
          <div className="call-readiness-empty">
            <MessageCircle size={28} />
            <div>
              <strong>No intake-ready submissions yet.</strong>
              <p>Once enquiries include enough detail for follow-up, they&apos;ll appear here.</p>
            </div>
          </div>
        ) : (
          <div className="call-readiness-grid">
            {filteredLeads.map((lead) => {
              const drr = resolveDrr(lead);
              const storeMeta = resolveStoreMeta(lead);
              const created = formatRelativeTime(lead.createdAt);
              const email = lead.lead?.email || lead.customerEmail || lead.email;
              const phone = lead.lead?.phone || lead.customerPhone || lead.phone;
              const name = lead.lead?.name || lead.customerName || lead.name || "Submission";
              const status = getLeadStatus(drr);
              const intakeLead = isIntakeLead(lead);
              const statusLabel = intakeLead
                ? status === "ready"
                  ? "Ready for follow-up"
                  : status === "mismatch"
                  ? "Potential blocker"
                  : "Missing key detail"
                : status === "ready"
                ? "Ready to Contact"
                : status === "mismatch"
                ? "Likely Mismatch"
                : "Needs Clarification";
              const clientSummaryText = buildClientSummary({
                name,
                storeMeta,
                drr,
              });
              const candidateSummaryText = buildCandidateSummary({
                name,
                storeMeta,
                drr,
              });
              const isArchived = Boolean(lead.archived || lead.lead?.archived);
              return (
                <div key={lead.orderId || lead.leadId} className="call-readiness-card">
                  <div className="call-readiness-card-header">
                    <div>
                      <div className="call-readiness-name">
                        <h4>{name}</h4>
                        {isArchived ? <span className="archived-badge">Archived</span> : null}
                      </div>
                      <span>{email || "—"}</span>
                      <div className="call-readiness-status">
                        <button
                          type="button"
                          className={`call-readiness-pill ${status}${
                            activeStatusFilter === status ? " is-active" : ""
                          }`}
                          onClick={() => handleStatusFilter(status)}
                        >
                          <span className="call-readiness-dot" />
                          {statusLabel}
                        </button>
                      </div>
                    </div>
                    <div className="call-readiness-meta">
                      <span>
                        Intake link:{" "}
                        {storeMeta.url ? (
                          <button
                            type="button"
                            className="copy-link-btn copy-link-btn--text"
                            onClick={() =>
                              window.open(storeMeta.url, "_blank", "noopener,noreferrer")
                            }
                          >
                            {storeMeta.name}
                          </button>
                        ) : (
                          storeMeta.name
                        )}
                      </span>
                      <span>{created}</span>
                    </div>
                  </div>
                  <div className="call-readiness-contact">
                    <span>{phone || "—"}</span>
                    <button
                      type="button"
                      className="copy-link-btn copy-summary-btn"
                      onClick={() =>
                        handleCopySummary(
                          `client-${lead.orderId || lead.leadId}`,
                          clientSummaryText
                        )
                      }
                    >
                      {copiedSummaryId === `client-${lead.orderId || lead.leadId}`
                        ? "Copied"
                        : "Copy client summary"}
                    </button>
                    <button
                      type="button"
                      className="copy-link-btn copy-summary-btn secondary"
                      onClick={() =>
                        handleCopySummary(
                          `candidate-${lead.orderId || lead.leadId}`,
                          candidateSummaryText
                        )
                      }
                    >
                      {copiedSummaryId === `candidate-${lead.orderId || lead.leadId}`
                        ? "Copied"
                        : "Copy learner summary"}
                    </button>
                    {onArchiveLead && (
                      <button
                        type="button"
                        className="copy-link-btn"
                        onClick={() => onArchiveLead(lead, !isArchived)}
                      >
                        {isArchived ? "Unarchive" : "Archive"}
                      </button>
                    )}
                  </div>
                  <div className="call-readiness-sections">
                    {intakeLead ? (
                      <>
                        <div className="call-readiness-section">
                          <span className="call-readiness-title">Enquiry summary</span>
                          <ul>
                            <li>Status: {statusLabel}</li>
                            {uniqueList(drr?.intake_profile?.missing_info || []).length ? (
                              <li>
                                Missing details:{" "}
                                {uniqueList(drr?.intake_profile?.missing_info || []).join(", ")}
                              </li>
                            ) : null}
                            {drr?.summary ? <li>{drr.summary}</li> : null}
                            {(drr?.next_steps || []).length ? (
                              (drr?.next_steps || []).map((item, idx) => (
                                <li key={`next-step-${idx}`}>{item}</li>
                              ))
                            ) : (
                              <li>Review the enquiry and confirm the best next step.</li>
                            )}
                          </ul>
                        </div>
                        <div className="call-readiness-section">
                          <span className="call-readiness-title">Key details</span>
                          <ul>
                            {intakeFieldRows(drr).length ? (
                              intakeFieldRows(drr).map(([label, value], idx) => (
                                <li key={`intake-detail-${idx}`}>{label}: {value}</li>
                              ))
                            ) : (
                              <li>Not provided</li>
                            )}
                          </ul>
                        </div>
                        <div className="call-readiness-section">
                          <span className="call-readiness-title">Potential blockers</span>
                          <ul>
                            {uniqueList(drr?.potential_blockers || []).length ? (
                              uniqueList(drr?.potential_blockers || []).map((item, idx) => (
                                <li key={`blocker-${idx}`}>{item}</li>
                              ))
                            ) : (
                              <li>None noted</li>
                            )}
                          </ul>
                        </div>
                      </>
                    ) : (
                      <>
                        {(drr?.summary || (drr?.next_steps || []).length) && (
                          <div className="call-readiness-section">
                            <span className="call-readiness-title">Decision summary</span>
                            <ul>
                              {(() => {
                                const blockers = drr?.potential_blockers || [];
                                const hasConstraints = (drr?.key_constraints || []).length > 0;
                                const hasAvailability = (drr?.availability_compensation || []).length > 0;
                                const hasExperience = (drr?.relevant_experience || []).length > 0;
                                const missing = [];
                                if (!hasConstraints) missing.push("Eligibility");
                                if (!hasAvailability) missing.push("Start & delivery");
                                if (!hasExperience) missing.push("Prior attainment");
                                let verdict = "Likely eligible";
                                if (blockers.length) verdict = "Needs review (flags detected)";
                                else if (missing.length) verdict = "Missing info";
                                return (
                                  <>
                                    <li>Eligibility verdict: {verdict}</li>
                                    {missing.length ? (
                                      <li>Missing info: {missing.join(", ")}</li>
                                    ) : null}
                                  </>
                                );
                              })()}
                              {drr?.summary ? <li>{drr.summary}</li> : null}
                              {(drr?.next_steps || []).length ? (
                                (drr?.next_steps || []).map((item, idx) => (
                                  <li key={`next-step-${idx}`}>{item}</li>
                                ))
                              ) : null}
                              {!drr?.summary && !(drr?.next_steps || []).length ? (
                                <li>Not provided</li>
                              ) : null}
                            </ul>
                          </div>
                        )}
                        <div className="call-readiness-section">
                          <span className="call-readiness-title">Eligibility</span>
                          <ul>
                            {(() => {
                              const constraints = uniqueList(drr?.key_constraints || []);
                              const employerLine = constraints.find((item) =>
                                /employer|levy|sme|funding/i.test(String(item || ""))
                              );
                              const remaining = employerLine
                                ? constraints.filter((item) => item !== employerLine)
                                : constraints;
                              if (!constraints.length) return <li>Not provided</li>;
                              return (
                                <>
                                  {employerLine ? <li>{employerLine}</li> : null}
                                  {remaining.map((item, idx) => (
                                    <li key={`constraint-${idx}`}>{item}</li>
                                  ))}
                                </>
                              );
                            })()}
                          </ul>
                        </div>
                        <div className="call-readiness-section">
                          <span className="call-readiness-title">Start & delivery</span>
                          <ul>
                            {uniqueList(drr?.availability_compensation || []).length ? (
                              uniqueList(drr?.availability_compensation || []).map((item, idx) => (
                                <li key={`availability-${idx}`}>{item}</li>
                              ))
                            ) : (
                              <li>Not provided</li>
                            )}
                          </ul>
                        </div>
                        <div className="call-readiness-section">
                          <span className="call-readiness-title">Prior attainment</span>
                          <ul>
                            {uniqueList(drr?.relevant_experience || []).length ? (
                              uniqueList(drr?.relevant_experience || []).map((item, idx) => (
                                <li key={`experience-${idx}`}>{item}</li>
                              ))
                            ) : (
                              <li>Not provided</li>
                            )}
                          </ul>
                        </div>
                        <div className="call-readiness-section">
                          <span className="call-readiness-title">Evidence & compliance flags</span>
                          <ul>
                            {uniqueList(drr?.potential_blockers || []).length ? (
                              uniqueList(drr?.potential_blockers || []).map((item, idx) => (
                                <li key={`blocker-${idx}`}>{item}</li>
                              ))
                            ) : (
                              <li>None noted</li>
                            )}
                          </ul>
                        </div>
                        {uniqueList(drr?.fields?.other || []).length > 0 ? (
                          <div className="call-readiness-section">
                            <span className="call-readiness-title">Additional responses</span>
                            <ul>
                              {uniqueList(drr?.fields?.other || []).map((item, idx) => {
                                const label = String(item?.label || "").trim();
                                const value = String(item?.value || "").trim();
                                if (!label && !value) return null;
                                return (
                                  <li key={`other-${idx}`}>
                                    {label ? `${label}: ` : ""}
                                    {value || "Provided"}
                                  </li>
                                );
                              })}
                            </ul>
                          </div>
                        ) : null}
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default CallReadinessModal;
